/**
 * useInfiniteScroll Hook
 *
 * Uses IntersectionObserver to detect when a trigger element
 * is visible and automatically loads more content.
 */

import { useEffect, useRef, useCallback, useState } from 'react';

export interface UseInfiniteScrollOptions {
  enabled?: boolean;
  threshold?: number;
  rootMargin?: string;
  onLoadMore: () => void;
  hasMore: boolean;
  loading: boolean;
}

export interface UseInfiniteScrollReturn {
  triggerRef: React.RefObject<HTMLDivElement>;
  isIntersecting: boolean;
}

export function useInfiniteScroll(
  options: UseInfiniteScrollOptions
): UseInfiniteScrollReturn {
  const {
    enabled = true,
    threshold = 0.1,
    rootMargin = '100px',
    onLoadMore,
    hasMore,
    loading,
  } = options;

  const triggerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const onLoadMoreRef = useRef(onLoadMore);

  // Keep callback ref updated
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      setIsIntersecting(entry.isIntersecting);

      if (entry.isIntersecting && hasMore && !loading) {
        onLoadMoreRef.current();
      }
    },
    [hasMore, loading]
  );

  useEffect(() => {
    // Disconnect existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Don't create observer if disabled or no more items
    if (!enabled || !hasMore) {
      return;
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold,
      rootMargin,
    });

    // Observe trigger element
    const triggerElement = triggerRef.current;
    if (triggerElement) {
      observerRef.current.observe(triggerElement);
    }

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [enabled, hasMore, threshold, rootMargin, handleIntersection]);

  return {
    triggerRef,
    isIntersecting,
  };
}

export default useInfiniteScroll;
